
import { useTranslation } from 'react-i18next';

export default function MarginRequirementsSection() {
  const { t } = useTranslation();

  const marginLevels = [
    {
      level: t("markets.account.standard", { defaultValue: "Standard Account" }),
      icon: 'ri-user-line',
      color: 'blue',
      requirements: {
        gold: { leverage: '1:100', margin: '1%', minDeposit: '$500' },
        silver: { leverage: '1:20', margin: '5%', minDeposit: '$500' }
      }
    },
    {
      level: t("markets.account.premium", { defaultValue: "Premium Account" }),
      icon: 'ri-vip-crown-line',
      color: 'purple',
      requirements: {
        gold: { leverage: '1:100', margin: '1%', minDeposit: '$5,000' },
        silver: { leverage: '1:50', margin: '2%', minDeposit: '$5,000' }
      }
    },
    {
      level: t("markets.account.vip", { defaultValue: "VIP Account" }),
      icon: 'ri-medal-line',
      color: 'yellow',
      requirements: {
        gold: { leverage: '1:500', margin: '0.2%', minDeposit: '$25,000' },
        silver: { leverage: '1:100', margin: '1%', minDeposit: '$25,000' }
      }
    }
  ];

  const marginCalculator = {
    gold: { price: 4950, lotSize: 100 },
    silver: { price: 74.5, lotSize: 5000 }
  };

  return (
    <section className="py-20 bg-gradient-to-br from-gray-50 to-white">
      <div className="container mx-auto px-6">
        <div className="text-center mb-16">
          <h2 className="text-5xl font-bold text-gray-900 mb-6">
            {t('markets.mr')}
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            {t('markets.lo')}
          </p>
        </div>

        {/* Account Types */}
        <div className="grid md:grid-cols-3 gap-8 mb-16">
          {marginLevels.map((account, index) => (
            <div key={index} className="bg-white rounded-3xl p-8 border border-gray-200 hover:shadow-xl transition-all duration-300">
              <div className="text-center mb-6">
                <div className={`w-16 h-16 bg-gradient-to-r from-${account.color}-400/20 to-${account.color}-600/20 rounded-2xl flex items-center justify-center border border-${account.color}-400/40 mx-auto mb-4`}>
                  <i className={`${account.icon} text-2xl text-${account.color}-600`}></i>
                </div>
                <h3 className="text-xl font-bold text-gray-900">{account.level}</h3>
              </div>

              <div className="space-y-4">
                <div className="bg-gray-50 rounded-xl p-4">
                  <h4 className="font-semibold text-gray-900 mb-3 flex items-center">
                    <i className="ri-copper-coin-line text-yellow-500 mr-2"></i>
                    {t('markets.contractSections.contractSize.items.xauusd.label')}
                  </h4>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-600">{t('markets.leverage')}:</span>
                      <span className="font-semibold">{account.requirements.gold.leverage}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">{t('markets.margin')}:</span>
                      <span className="font-semibold">{account.requirements.gold.margin}</span>
                    </div>
                  </div>
                </div>

                <div className="bg-gray-50 rounded-xl p-4">
                  <h4 className="font-semibold text-gray-900 mb-3 flex items-center">
                    <i className="ri-copper-diamond-line text-gray-500 mr-2"></i>
                    {t('markets.contractSections.contractSize.items.xagusd.label')}
                  </h4>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-600">{t('markets.leverage')}:</span>
                      <span className="font-semibold">{account.requirements.silver.leverage}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">{t('markets.margin')}:</span>
                      <span className="font-semibold">{account.requirements.silver.margin}</span>
                    </div>
                  </div>
                </div>

                <div className={`bg-gradient-to-r from-${account.color}-50 to-${account.color}-100 rounded-xl p-4 border border-${account.color}-200`}>
                  <div className="text-sm text-gray-600 mb-1">{t('markets.mind')}</div>
                  <div className={`text-lg font-bold text-${account.color}-700`}>{account.requirements.gold.minDeposit}</div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Margin Calculator */}
        <div className="bg-white rounded-3xl p-8 border border-gray-200">
          <h3 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
            <i className="ri-calculator-line text-blue-600 mr-3"></i>
            {t('markets.mc')}
          </h3>

          <div className="grid md:grid-cols-2 gap-8">
            <div className="bg-gradient-to-br from-yellow-50 to-yellow-100 rounded-xl p-6 border border-yellow-200">
              <h4 className="text-lg font-bold text-gray-900 mb-4 flex items-center">
                <i className="ri-copper-coin-line text-yellow-600 mr-2"></i>
                {t('markets.gmc')}
              </h4>
              <div className="space-y-3">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">{t('markets.cp')}:</span>
                  <span className="font-semibold">${marginCalculator.gold.price}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">{t('markets.lotSize')}:</span>
                  <span className="font-semibold">{marginCalculator.gold.lotSize} oz</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">{t('markets.cv')}:</span>
                  <span className="font-semibold">${(marginCalculator.gold.price * marginCalculator.gold.lotSize).toLocaleString()}</span>
                </div>
                <hr className="border-yellow-300" />
                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">{t('markets.margin')}(1:100):</span>
                    <span className="font-bold text-blue-600">${(marginCalculator.gold.price * marginCalculator.gold.lotSize / 100).toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">{t('markets.margin')}(1:200):</span>
                    <span className="font-bold text-purple-600">${(marginCalculator.gold.price * marginCalculator.gold.lotSize / 200).toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">{t('markets.margin')}(1:400):</span>
                    <span className="font-bold text-yellow-600">${(marginCalculator.gold.price * marginCalculator.gold.lotSize / 400).toLocaleString()}</span>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-xl p-6 border border-gray-200">
              <h4 className="text-lg font-bold text-gray-900 mb-4 flex items-center">
                <i className="ri-copper-diamond-line text-gray-600 mr-2"></i>
                {t('markets.smc')}
              </h4>
              <div className="space-y-3">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">{t('markets.cv')}:</span>
                  <span className="font-semibold">${marginCalculator.silver.price}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">{t('markets.lotSize')}:</span>
                  <span className="font-semibold">{marginCalculator.silver.lotSize.toLocaleString()} oz</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">{t('markets.cv')}:</span>
                  <span className="font-semibold">${(marginCalculator.silver.price * marginCalculator.silver.lotSize).toLocaleString()}</span>
                </div>
                <hr className="border-gray-300" />
                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">{t('markets.margin')}(1:100):</span>
                    <span className="font-bold text-blue-600">${(marginCalculator.silver.price * marginCalculator.silver.lotSize / 100).toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">{t('markets.margin')}(1:200):</span>
                    <span className="font-bold text-purple-600">${(marginCalculator.silver.price * marginCalculator.silver.lotSize / 200).toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">{t('markets.margin')}(1:400):</span>
                    <span className="font-bold text-yellow-600">${(marginCalculator.silver.price * marginCalculator.silver.lotSize / 400).toLocaleString()}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
